/**
 * TasksInlineView — 채널 Tasks 인라인 뷰 (통합 보드)
 *
 * 단일 Tasks 보드 안에서 진행/완료를 함께 다룬다.
 * - 상태 필터가 '완료'(done)일 때: 검색/필터 패널 노출 (CompletedSearchPanel)
 * - 그 외: 카드 목록 (TaskCardList)
 *
 * 완료된 태스크의 AI 완료 문서는 별도 보드가 아닌 원본 카드의 metadata.completion 에 저장된다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  ListTodo, Loader2, Plus, Eye, Bell,
} from 'lucide-react';
import { Board, Post, TaskStatus, getTaskMetadata, TASK_STATUS_CONFIG, getTaskStatusLabel, canUseAllMention } from '../../models/Board';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { fetchPosts, fetchPost, createBoard, fetchTasksBoard } from '../../services/BoardService';
import { TaskEmitter } from './TaskEmitter';
import { My } from '../../services/My';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import TasksOnboarding from './TasksOnboarding';
import TaskCardList, { TaskCardListSkeleton } from './TaskCardList';
import TaskCardDetail from './TaskCardDetail';
import RecordListView from './RecordListView';
import TaskCreateModal from './TaskCreateModal';
import BoardNotifyAllSettingsModal from '../board/BoardNotifyAllSettingsModal';
import { MyNotifs } from '../../services/MyNotifs';
import { useAndroidBackHandler } from '../../hooks/useAndroidBackHandler';

type TaskSortBy = 'activity' | 'due_date' | 'created';

interface TasksInlineViewProps {
  workspaceId: string;
  roomId: string;
  /** 채널 방장 UID — 태스크는 멤버 누구나 시작하되, 보드 소유·관리 주체는 방장으로 고정 */
  channelOwnerId?: string;
  /** 외부에서 자동으로 열 태스크 postId */
  pendingPostId?: string | null;
  /** pendingPostId 소비 완료 콜백 */
  onPendingPostConsumed?: () => void;
}

const TasksInlineView: React.FC<TasksInlineViewProps> = ({
  workspaceId,
  roomId,
  channelOwnerId,
  pendingPostId,
  onPendingPostConsumed,
}) => {
  // 보드 상태
  const [tasksBoard, setTasksBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  // 카드(posts) 로드 완료 여부 — 로드 전엔 빈 목록 대신 로딩 표시 (깜빡 방지)
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [activating, setActivating] = useState(false);
  const [showNotifyAll, setShowNotifyAll] = useState(false);
  // 알림 설정 모달에 직접 넘길 보드 (태스크 보드 1개) — useMemo 로 안정화(useEffect 무한 루프 방지)
  const notifyBoards = useMemo(() => (tasksBoard ? [tasksBoard] : []), [tasksBoard]);
  // @전체 멘션 권한 (태스크 보드 정책 기반)
  const myRole = useMyRole();
  const wsSettings = useWorkspaceSettings();
  const canMentionAll = tasksBoard ? canUseAllMention(tasksBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids) : false;

  // 카드 목록 상태
  const [posts, setPosts] = useState<Post[]>([]);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all' | 'incomplete'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all'); // 'all' | 'unassigned' | uid
  const [authorFilter, setAuthorFilter] = useState<string>('all');     // 'all' | uid
  const [watchingOnly, setWatchingOnly] = useState(false);             // 내가 워치 중인 태스크만
  const [sortBy, setSortBy] = useState<TaskSortBy>('activity');

  // 직접 태스크 생성 모달
  const [showCreateModal, setShowCreateModal] = useState(false);

  // 카드 상세 선택 상태
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  // 상세 오버레이 닫기 가드 (수정 중·내부 모달 열림 시 바깥클릭/ESC 무시)
  const [detailCloseGuarded, setDetailCloseGuarded] = useState(false);

  // 통지센터 알림(미읽음)이 있는 태스크 카드 → 제목 옆 오렌지 점 (source='task', MyNotifs 실시간 구독)
  const [notifiedPostIds, setNotifiedPostIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const compute = () => setNotifiedPostIds(new Set(
      MyNotifs.Notifications.filter(n => n.isNew && n.source === 'task' && n.postId).map(n => n.postId!),
    ));
    compute();
    return MyNotifs.subscribeToNotificationUpdates(compute);
  }, []);

  /** 채널 멤버 목록 (담당자 선택용) */
  const channelMembers = useMemo(() => {
    const room = My.Rooms.get(roomId);
    if (!room) return [];
    return room.users.map(u => ({ id: u.id, name: u.name, photoUrl: u.photoUrl }));
  }, [roomId]);

  /** Tasks 보드 로드 */
  const loadBoards = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const tasks = await fetchTasksBoard(roomId);
      setTasksBoard(tasks);
    } catch (err) {
      console.error('[TasksInlineView] 보드 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, roomId]);

  /** 카드 목록 로드 */
  const loadCards = useCallback(async (boardId: string) => {
    try {
      const result = await fetchPosts(boardId, { page: 0, pageSize: 100 });
      setPosts(result.posts);
    } catch (err) {
      console.error('[TasksInlineView] 카드 로드 실패:', err);
    } finally {
      setCardsLoaded(true);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  // Tasks 보드 변경 시 카드 로드
  useEffect(() => {
    if (tasksBoard) {
      setCardsLoaded(false);
      loadCards(tasksBoard.id);
    }
  }, [tasksBoard?.id, loadCards]);

  /** "시작하기" 핸들러 — Tasks 보드 단일 생성 (records 보드 없음)
   *  태스크는 채널 멤버 누구나 시작 가능하나, 보드 소유·관리 주체는 채널 방장으로 고정한다.
   *  (owner_id 를 시작한 멤버로 두면 그 멤버만 보드를 관리하게 되어 부적절 — RLS 도 owner_id=방장 강제) */
  const handleActivate = async () => {
    const ownerId = channelOwnerId ?? My.Rooms.get(roomId)?.creatorId;
    if (!ownerId) {
      console.error('[TasksInlineView] 채널 방장 정보 없음 — 태스크 보드 생성 불가');
      return;
    }
    setActivating(true);
    try {
      const existing = await fetchTasksBoard(roomId);
      const finalBoard = existing ?? await createBoard({
        workspace_id: workspaceId,
        name: 'Tasks',
        description: t`Work tasks captured from chat conversations (incl. completion docs)`,
        type: 'tasks',
        scope: 'channel',
        channel_id: roomId,
        owner_id: ownerId,
      });
      setTasksBoard(finalBoard);
    } catch (err) {
      console.error('[TasksInlineView] 활성화 실패:', err);
      toast({ title: t`Failed to start tasks`, description: (err as Error).message, variant: 'destructive' });
    } finally {
      setActivating(false);
    }
  };

  // TaskEmitter 'created' 이벤트 리스너 — 카드 생성 후 목록 자동 갱신
  useEffect(() => {
    const handleCreated = (createdRoomId: string) => {
      if (createdRoomId === roomId && tasksBoard) {
        loadCards(tasksBoard.id);
      }
    };
    TaskEmitter.on('created', handleCreated);
    return () => {
      TaskEmitter.removeListener('created', handleCreated);
    };
  }, [roomId, tasksBoard?.id, loadCards]);

  // 상세 오버레이 닫기 (목록 새로고침 포함)
  const closeDetail = useCallback(() => {
    setSelectedPost(null);
    setDetailCloseGuarded(false);
    if (tasksBoard) loadCards(tasksBoard.id);
  }, [tasksBoard, loadCards]);

  // 상세 오버레이: ESC 로 닫기 (수정 중·내부 모달 열림 시 무시)
  useEffect(() => {
    if (!selectedPost) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || detailCloseGuarded) return;
      // 멘션 드롭다운이 열려 있으면 모달을 닫지 않음 — 그 ESC로 드롭다운이 닫히고, 다음 ESC에 모달이 닫힘
      if (document.querySelector('.nextpod-mentions__suggestions')) return;
      closeDetail();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedPost, detailCloseGuarded, closeDetail]);

  // 안드로이드 하드웨어 백버튼: 상세 열림 중엔 목록으로 복귀 (ESC 와 동일 의미론).
  // 가드 중(수정·내부 모달)에는 닫지 않되 이벤트는 소비 — 실수로 "한 번 더 누르면 종료"로
  // 빠져 편집 내용을 잃는 것을 방지한다.
  useAndroidBackHandler(!!selectedPost, () => {
    if (!detailCloseGuarded) closeDetail();
    return true;
  });

  // pendingPostId 처리: 외부에서 특정 태스크를 자동으로 열기
  useEffect(() => {
    if (!pendingPostId || !tasksBoard) return;
    const existingPost = posts.find(p => p.id === pendingPostId);
    if (existingPost) {
      setSelectedPost(existingPost);
      onPendingPostConsumed?.();
    } else {
      fetchPost(pendingPostId).then(post => {
        if (post) {
          setSelectedPost(post);
          loadCards(tasksBoard.id);
        }
        onPendingPostConsumed?.();
      }).catch(() => {
        onPendingPostConsumed?.();
      });
    }
  }, [pendingPostId, tasksBoard?.id, posts.length]);

  // 진행 중 카드 필터 (status !== 'done')
  const isCompletedView = statusFilter === 'done';

  const filteredPosts = posts
    .filter(post => {
      const meta = getTaskMetadata(post);
      // 상태 필터
      if (statusFilter === 'incomplete') {
        // '진행 중' 필터: done 제외
        if (!meta || meta.status === 'done') return false;
      } else if (statusFilter === 'all') {
        // '전체': done 포함 모든 상태 표시 (라벨대로 동작)
        // 별도 RecordListView(완료 검색 패널)는 'done' 필터에서만 진입
      } else if (statusFilter !== 'done') {
        if (meta?.status !== statusFilter) return false;
      }
      // 담당자 필터
      if (assigneeFilter !== 'all') {
        const assigneeUid = meta?.assignee?.uid;
        if (assigneeFilter === 'unassigned') {
          if (assigneeUid) return false;
        } else {
          if (assigneeUid !== assigneeFilter) return false;
        }
      }
      // 작성자 필터
      if (authorFilter !== 'all') {
        if (post.author?.uid !== authorFilter) return false;
      }
      // 워쳐 필터 — 내가 워치 중인 태스크만
      if (watchingOnly) {
        if (!meta?.watchers?.includes(My.user.id)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const metaA = getTaskMetadata(a);
      const metaB = getTaskMetadata(b);
      switch (sortBy) {
        case 'activity':
          return (metaB?.last_activity_at ?? b.created_at).localeCompare(
            metaA?.last_activity_at ?? a.created_at
          );
        case 'due_date': {
          const duaA = metaA?.due_date ?? '9999-12-31';
          const duaB = metaB?.due_date ?? '9999-12-31';
          return duaA.localeCompare(duaB);
        }
        case 'created':
          return b.created_at.localeCompare(a.created_at);
        default:
          return 0;
      }
    });

  // 로딩 상태
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="size-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  // 미활성 상태: 온보딩
  if (!tasksBoard) {
    return (
      <TasksOnboarding
        onActivate={handleActivate}
        activating={activating}
      />
    );
  }

  // 카드(posts) 로드 전 → 카드 스켈레톤 (빈 목록 깜빡 방지, 채팅창처럼)
  if (!cardsLoaded) {
    return <TaskCardListSkeleton />;
  }

  // 활성 상태: 통합 카드 목록 (진행/완료 동일 보드)
  return (
    <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* 헤더 — 카운트 + "New Task" */}
      {(
        <div className="relative flex items-center mx-3.5 pt-3 gap-2 shrink-0 border-b border-border sm:mx-[18px] sm:pt-[18px] sm:gap-2.5">
          <div className="flex items-center gap-2 pb-2 sm:gap-2.5 sm:pb-3 min-w-0">
            <ListTodo className="size-3.5 text-primary shrink-0 sm:size-4" />
            <span className="text-body font-semibold text-[color:rgb(var(--text-title))] tracking-tight truncate min-w-0 sm:text-heading">
              <Trans>Tasks</Trans>
            </span>
          </div>

          <div className="flex-1" />

          {tasksBoard && (
            <button
              type="button"
              onClick={() => setShowNotifyAll(true)}
              className="mb-2 sm:mb-2.5 shrink-0 size-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              title={t`Notification settings`}
            >
              <Bell className="size-4" />
            </button>
          )}

          <Button
            size="sm"
            className="mb-2 sm:mb-2.5 shrink-0"
            onClick={() => setShowCreateModal(true)}
            title={t`New Task`}
          >
            <Plus className="size-3.5 mr-1.5" />
            <Trans>New Task</Trans>
          </Button>
        </div>
      )}

      {/* 필터/정렬 바 — 모바일도 가로 스크롤 대신 줄바꿈 (숨김 스크롤은 마지막 칩이 잘려 보임) */}
      {(
        <div className="relative flex flex-wrap items-center gap-1.5 px-3.5 pt-2.5 pb-2 shrink-0 sm:gap-2 sm:px-[18px] sm:pt-3.5 sm:pb-2.5">
          {/* 상태 필터 — 'done' 선택 시 완료 검색 패널로 전환 */}
          <Select
            value={statusFilter}
            onValueChange={v => setStatusFilter(v as TaskStatus | 'all' | 'incomplete')}
          >
            <SelectTrigger className={cn(
              "h-[30px] w-auto min-w-0 gap-1.5 border-border text-label px-3 py-0 shadow-none shrink-0",
              isCompletedView ? "text-primary border-primary/40 bg-primary/8" : "text-muted-foreground",
            )}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t`All`}</SelectItem>
              <SelectItem value="incomplete">{t`Incomplete`}</SelectItem>
              {Object.entries(TASK_STATUS_CONFIG).map(([key]) => (
                <SelectItem key={key} value={key}>{getTaskStatusLabel(key as TaskStatus)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* 담당자/정렬 — 완료 검색 모드에서는 숨김 (완료 검색 패널이 자체 필터 보유) */}
          {!isCompletedView && (
            <>
              <Select
                value={assigneeFilter}
                onValueChange={v => setAssigneeFilter(v)}
              >
                <SelectTrigger className="h-[30px] w-auto min-w-0 max-w-[150px] gap-1.5 border-border text-label text-muted-foreground px-3 py-0 shadow-none shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`All Assignees`}</SelectItem>
                  <SelectItem value="unassigned">{t`Unassigned`}</SelectItem>
                  {channelMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={authorFilter}
                onValueChange={v => setAuthorFilter(v)}
              >
                <SelectTrigger className="h-[30px] w-auto min-w-0 max-w-[150px] gap-1.5 border-border text-label text-muted-foreground px-3 py-0 shadow-none shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`All Authors`}</SelectItem>
                  {channelMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* 워쳐 토글 — 내가 워치 중인 태스크만 */}
              <button
                type="button"
                onClick={() => setWatchingOnly(v => !v)}
                className={cn(
                  "flex items-center gap-1.5 h-[30px] px-3 rounded-control border text-label shrink-0 transition-colors",
                  watchingOnly
                    ? "text-primary border-primary/40 bg-primary/8"
                    : "text-muted-foreground border-border hover:bg-accent",
                )}
              >
                <Eye className="size-3.5" />
                <Trans>Watching</Trans>
              </button>

              <div className="hidden sm:block sm:flex-1" />

              <Select
                value={sortBy}
                onValueChange={v => setSortBy(v as TaskSortBy)}
              >
                <SelectTrigger className="h-[30px] w-auto min-w-0 gap-1.5 border-border text-label text-muted-foreground px-3 py-0 shadow-none shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activity">{t`Recent Activity`}</SelectItem>
                  <SelectItem value="due_date">{t`Due Date`}</SelectItem>
                  <SelectItem value="created">{t`Created`}</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      )}

      {/* 컨텐츠 영역 */}
      <div className="flex-1 flex flex-col min-h-0 relative">
        {/* 목록은 항상 마운트 — 상세 오버레이 뒤에 비침 */}
        {isCompletedView ? (
          // 완료 검색 모드: RecordListView는 완료된 카드(metadata.status='done')만 검색
          <RecordListView tasksBoard={tasksBoard} onPostClick={(post) => setSelectedPost(post)} />
        ) : (
          <TaskCardList
            posts={filteredPosts}
            onPostClick={(post) => setSelectedPost(post)}
            onRefresh={() => tasksBoard && loadCards(tasksBoard.id)}
            notifiedPostIds={notifiedPostIds}
          />
        )}

      </div>

      {/* 상세 오버레이 — 태스크 패널 안에만 갇힘(비차단). 좌측 채팅은 계속 사용 가능,
          태스크 영역 바깥 여백(백드롭) 클릭 또는 ESC 로 닫힘. 수정 중엔 닫기 버튼으로만. */}
      {selectedPost && (
        <div className="absolute inset-0 z-30 flex sm:items-center sm:justify-center animate-in fade-in duration-150">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => { if (!detailCloseGuarded) closeDetail(); }}
            aria-hidden
          />
          <div className="relative flex flex-col min-h-0 overflow-hidden bg-background w-full h-full sm:w-[calc(100%-5rem)] sm:max-w-[880px] sm:h-[calc(100%-2.5rem)] sm:max-h-[1000px] sm:rounded-panel sm:shadow-2xl">
            <TaskCardDetail
              post={selectedPost}
              channelMembers={channelMembers}
              roomId={roomId}
              onClose={closeDetail}
              onRefresh={() => tasksBoard && loadCards(tasksBoard.id)}
              onCloseGuardChange={setDetailCloseGuarded}
              canMentionAll={canMentionAll}
            />
          </div>
        </div>
      )}

      {/* 직접 태스크 생성 모달 */}
      <TaskCreateModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        mode="manual"
        roomId={roomId}
      />

      {/* 알림 설정 모달 — 이 채널의 태스크 보드만 (게시판과 동일 UI 재사용) */}
      <BoardNotifyAllSettingsModal
        open={showNotifyAll}
        onClose={() => setShowNotifyAll(false)}
        workspaceId={workspaceId}
        directBoards={notifyBoards}
      />
    </div>
  );
};

export default TasksInlineView;
